/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { sanitizeRichText } from './sanitizeMarkup';

const buffer = document.createElement('div');
const BLOCK_TAGS = new Set([
  'BLOCKQUOTE',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'OL',
  'P',
  'PRE',
  'UL',
]);

export default function stripHTML(string) {
  string = string.replace(/(<br\/>)/g, ' ');
  // @todo: implement a cheaper way to strip markup.
  buffer.innerHTML = sanitizeRichText(string);

  if (buffer.querySelector('ul, ol')) {
    return getSemanticText(buffer)
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  return buffer.textContent;
}

function getSemanticText(node, depth = 0) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }

  if (!(node instanceof HTMLElement)) {
    return '';
  }

  if (node.tagName === 'BR') {
    return ' ';
  }

  if (node.tagName === 'UL' || node.tagName === 'OL') {
    const items = Array.from(node.children).filter(
      (child) => child.tagName === 'LI'
    );

    return items
      .map((item, index) => {
        const marker = node.tagName === 'OL' ? `${index + 1}.` : '•';
        const itemText = Array.from(item.childNodes)
          .filter(
            (child) =>
              !(child instanceof HTMLElement) ||
              (child.tagName !== 'UL' && child.tagName !== 'OL')
          )
          .map((child) => getSemanticText(child, depth))
          .join('')
          .trim();
        const nestedText = Array.from(item.children)
          .filter((child) => child.tagName === 'UL' || child.tagName === 'OL')
          .map((child) => getSemanticText(child, depth + 1))
          .filter(Boolean)
          .join('\n');
        const currentLine = `${'  '.repeat(depth)}${marker} ${itemText}`;

        return nestedText ? `${currentLine}\n${nestedText}` : currentLine;
      })
      .join('\n');
  }

  const children = Array.from(node.childNodes);
  const hasBlockChildren = children.some(isBlockElement);
  let result = '';
  let previousWasBlock = false;

  for (const child of children) {
    if (
      hasBlockChildren &&
      child.nodeType === Node.TEXT_NODE &&
      !child.textContent?.trim()
    ) {
      continue;
    }

    const childText = getSemanticText(child, depth);
    if (!childText) {
      continue;
    }

    const currentIsBlock = isBlockElement(child);
    if (
      result &&
      (previousWasBlock || currentIsBlock) &&
      !result.endsWith('\n') &&
      !childText.startsWith('\n')
    ) {
      result += '\n';
    }

    result += childText;
    previousWasBlock = currentIsBlock;
  }

  return result;
}

function isBlockElement(node) {
  return node instanceof HTMLElement && BLOCK_TAGS.has(node.tagName);
}
